"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";

import { Product as ProductType } from "@/@types";
import useWishlist from "@/hooks/useWishlist";

import styles from "./Product.module.scss";
import { IoMdHeart, IoMdHeartEmpty } from "react-icons/io";
import { useModalContext } from "@/contexts/ModalContext";
import { formatCurrency, defineImageUrl } from "@/utils";

interface Props {
  product: ProductType;
}

export default function Product(props: Props) {
  const { product } = props;
  const { showModal } = useModalContext();

  const { handleAddWishlist, checkIfIsInWishlist } = useWishlist();

  const defineImage = useMemo(() => {
    return (
      defineImageUrl(product?.image?.url) || "/images/panela.jpg"
    );
  }, [product]);

  return (
    <motion.div
      className={styles.Product}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.Product__badges}>
        {product?.raw?.DESTAQUESITE === "1" && <span>Novo</span>}
        {product?.raw?.EXCLUSIVO === "1" && <span>Exclusivo</span>}
        {product?.customizable && <span>Customizável</span>}
      </div>
      <div className={styles.Product__link}>
        <Link
          prefetch={false}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            showModal(product);
          }}
        >
          <div className={styles.Product__image}>
            <Image
              src={defineImage}
              alt="Product Image"
              width={150}
              height={150}
              style={{
                objectFit: "contain",
              }}
            />
          </div>
          {product?.related_products?.length > 0 ? (
            <p className={styles.Product__sku}>
              {product?.related_products?.length + 1} OPÇÕES
            </p>
          ) : (
            <p className={styles.Product__sku}>{product?.sku}</p>
          )}

          <h2 className={styles.Product__title}>{product?.name}</h2>
          {process.env.NEXT_PUBLIC_SHOWROOM === "true" && (
            <p className={styles.Product__price}>
              {formatCurrency(product?.price_rent || 0)}
            </p>
          )}
        </Link>
      </div>

      <div className={styles.Product__menu}>
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 1.2 }}
          onClick={() => handleAddWishlist(product)}
          className={styles.Product__wishlist}
        >
          {checkIfIsInWishlist(product) ? (
            <IoMdHeart size={18} color="var(--primary)" />
          ) : (
            <IoMdHeartEmpty size={18} color="var(--primary)" />
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}
